import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Alert, AlertService } from '@michelin/theme';
import { forkJoin } from 'rxjs';
import { AutomakerDto } from 'src/app/shared/models/automaker/automaker-dto';
import { CompanyDto } from 'src/app/shared/models/company/company-dto';
import { DistributorDto } from 'src/app/shared/models/distributor/distributor-dto';
import { UserDto } from 'src/app/shared/models/user/user-dto';
import { UserProfileDto, UserProfileSimpleDto } from 'src/app/shared/models/user/user-profile-dto';
import { AutomakerService } from 'src/app/shared/services/automaker/automaker.service';
import { DistributorService } from 'src/app/shared/services/distributor/distributor.service';
import { UserProfileService } from 'src/app/shared/services/user-profile/user-profile.service';

@Component({
    selector: 'app-user-form',
    templateUrl: './user-form.component.html',
    standalone: false
})
export class UserFormComponent implements OnInit {
  @Input() create: boolean = false;
  @Input() readonly: boolean = false;
  @Input() formData: UserDto = null;
  @Output() change = new EventEmitter();
  @Output() loadingChange = new EventEmitter();

  loading = false;
  loadedAutomakers = false;
  loadedDistributors = false;

  form: FormGroup = null;

  data = {
    userProfiles: [] as UserProfileSimpleDto[],
    automakers: [] as CompanyDto[],
    distributors: [] as CompanyDto[]
  };

  constructor(
    public readonly builder: FormBuilder,
    private readonly automakerService: AutomakerService,
    private readonly distributorService: DistributorService,
    private readonly userProfileService: UserProfileService,
    private readonly alertService: AlertService
  ) {
    this.form = this.builder.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      type: ['', Validators.required],
      company: [''],
      userProfile: ['', Validators.required],
      phones: [''],
      password: [''],
      passwordConfirm: [''],
      enabled: [true]
    });
  }

  get type() {
    return this.form.get('type') as FormControl<string>;
  }

  get company() {
    return this.form.get('company') as FormControl<string>;
  }

  get userProfile() {
    return this.form.get('userProfile') as FormControl;
  }

  get profilesByType() {
    return this.data.userProfiles.filter((profile) => profile.userType == this.type.value);
  }

  setLoading(state: boolean) {
    this.loadingChange.emit(state);
  }

  profileDisplayName(data: UserProfileDto) {
    return data?.name || '';
  }

  companyDisplayName(data: CompanyDto) {
    return data?.companyName || '';
  }

  loadFormValues(data: UserDto) {
    // debugger
    this.form.patchValue({
      name: data.name,
      email: data.userId,
      type: data.userProfile.userType,
      userProfile: data.userProfile,
      company: data.company
    });
  }

  ngOnInit() {
    this.setLoading(true);

    forkJoin({
      userProfiles: this.userProfileService.findAll(),
      automakers: this.automakerService.findAll(),
      distributors: this.distributorService.findAll()
    })
      .subscribe({
        next: (result) => {
          this.data.userProfiles = result.userProfiles;
          this.data.automakers = result.automakers.map((item) => item.company);
          this.data.distributors = result.distributors.map((item) => item.company);
        },
        error: () => this.alertService.showAlert({ type: 'critical', content: 'Ocorreu um erro ao carregar as informações.' } as Alert)
      })
      .add(() => this.setLoading(false));

    this.form.valueChanges.subscribe((values) => this.change.emit({ form: this.form, values }));

    if (this.readonly) {
      this.form.disable();
    }

    if (this.create) {
      this.form.get('password').addValidators(Validators.required);
      this.form.get('passwordConfirm').addValidators(Validators.required);
    }

    if (this.formData != null) {
      this.userProfileService.findByType(this.formData.userProfile.userType).subscribe((values) => {
        this.data.userProfiles = values;
        this.loadFormValues(this.formData);
        if (typeof this.formData.enabled == 'undefined') {
          this.form.get('enabled').patchValue(false);
        }
      });
    }

    this.type.valueChanges.subscribe((value: string) => {
      this.userProfile.patchValue('');
    });
  }
}
